import React, { useState, useEffect } from 'react';
import { FiUserPlus, FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { BASE_URL, POST_DEVICE_CONFIG_BULK, GET_DEVICE_CONFIG, DELETE_DEVICE_CONFIG } from '../../utils/config';
import { useNavigate } from 'react-router-dom';

const AddClients = () => {
  const [devices, setDevices] = useState([{ systemID: '', systemName: '' }]);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingDevices, setExistingDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const token = localStorage.getItem('accessToken');
  const navigate = useNavigate();

  const [clientInfo, setClientInfo] = useState({});

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
      ipcRenderer = window.require('electron').ipcRenderer;
  }



  useEffect(() => {
    const fetchData = async () => {
      let client_info;

      if(isElectron()){
        client_info = await ipcRenderer.invoke('store-get', 'clientInfo'); 
      }

      console.log('clientInfo', client_info)
      setClientInfo(client_info || {})
    };

    fetchData();
  }, []);

  // Fetch existing devices
  useEffect(() => {
    const fetchDevices = async () => {

      setIsLoadingDevices(true);
      try {
        const response = await axios.get(`${BASE_URL}${GET_DEVICE_CONFIG}`, {
          headers: {
            'TenantName': 'Abcd',
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`
          },
        });

        if (response.status === 200) {
          setExistingDevices(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
        toast.error('Failed to fetch devices', { autoClose: 3000 });
      } finally {
        setIsLoadingDevices(false);
      }
    };

    fetchDevices();
  }, []);


  const addDevice = () => {
    setDevices([...devices, { systemID: '', systemName: '' }]);
  };

  const removeDevice = (index) => {
    if (devices.length > 1) {
      setDevices(devices.filter((_, i) => i !== index));
    }
  };

  const updateDevice = (index, field, value) => {
    const updated = [...devices];
    updated[index][field] = value;
    setDevices(updated);
  };

  const checkLicenseAvailability = () => {
    const maxLicenses = clientInfo?.No_Of_Licence || 0;
    if (maxLicenses === 0) {
      toast.error('License information not available', { autoClose: 3000 });
      return false;
    }

    const currentDeviceCount = existingDevices.length;
    const availableLicenses = maxLicenses - currentDeviceCount;

    if (availableLicenses <= 0) {
      toast.error(
        `No available licenses. Maximum ${maxLicenses} license(s) allowed. Currently using ${currentDeviceCount} license(s).`,
        { autoClose: 5000 }
      );
      return false;
    }

    return true;
  };

  const handleOpenAddModal = () => {
    if (!checkLicenseAvailability()) {
      return;
    }
    setShowAddDeviceModal(true);
  };

  const validateDevices = () => {
    for (let i = 0; i < devices.length; i++) {
      if (!devices[i].systemID.trim() || !devices[i].systemName.trim()) {
        toast.error(`Device ${i + 1}: Both System ID and System Name are required`, { autoClose: 3000 });
        return false;
      }
    }

    // Validate against maximum licenses
    const maxLicenses = clientInfo?.No_Of_Licence || 0;
    if (maxLicenses > 0) {
      const currentDeviceCount = existingDevices.length;
      const newDevicesCount = devices.filter(d => d.systemID.trim() && d.systemName.trim()).length;
      const totalAfterAdd = currentDeviceCount + newDevicesCount;

      if (totalAfterAdd > maxLicenses) {
        toast.error(
          `Cannot add ${newDevicesCount} device(s). Maximum ${maxLicenses} license(s) allowed. Currently using ${currentDeviceCount} license(s).`,
          { autoClose: 5000 }
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDevices()) {
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare device data
      const deviceData = devices.map((device) => ({
        systemID: device.systemID.trim(),
        systemName: device.systemName.trim(),
      }));

      const response = await axios.post(`${BASE_URL}${POST_DEVICE_CONFIG_BULK}`, deviceData, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        toast.success(`Successfully added ${deviceData.length} device(s)`, { autoClose: 3000 });
        setDevices([{ systemID: '', systemName: '' }]);
        setShowAddDeviceModal(false);
        // Refresh device list
        const refreshResponse = await axios.get(`${BASE_URL}${GET_DEVICE_CONFIG}`, {
          headers: {
            'TenantName': 'Abcd',
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`
          },
        });
        if (refreshResponse.status === 200) {
          setExistingDevices(refreshResponse.data.data);
        }
      } else {
        toast.error('Failed to add devices', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error submitting devices:', error);
      toast.error(error.response?.data?.message || 'Failed to add devices. Please try again.', { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDevice = async (sysID) => {
    // Find device name for better confirmation message
    const device = existingDevices.find(d => d.SystemID === sysID);
    const deviceName = device?.SystemName || sysID;

    // Confirm deletion with SweetAlert
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete device "${deviceName}" (${sysID})? You will be logged out after deletion.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}${DELETE_DEVICE_CONFIG}`,
        {},
        {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY || '',
            'Authorization': `Bearer ${token}`,
            'SysID': sysID,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        toast.success('Device deleted successfully', { autoClose: 3000 });
        // Logout after successful delete

        const refreshResponse = await axios.get(`${BASE_URL}${GET_DEVICE_CONFIG}`, {
          headers: {
            'TenantName': 'Abcd',
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`
          },
        });
        if (refreshResponse.status === 200) {
          setExistingDevices(refreshResponse.data.data);
        }
      } else {
        toast.error('Failed to delete device', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error(error.response?.data?.message || 'Failed to delete device. Please try again.', { autoClose: 3000 });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="box-border w-full min-w-0 max-w-full p-4 md:p-6">
      <ToastContainer 
        position="top-right" 
        autoClose={3000}
      />
      <style>{`
        .Toastify__toast-container {
          z-index: 10000 !important;
        }
        .Toastify__toast {
          z-index: 10000 !important;
        }
      `}</style>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FiUserPlus size={32} className="text-blue-600" />
          Manage Devices
        </h1>
        <p className="text-gray-500 mt-2">Add and manage device configurations</p>
        {clientInfo?.No_Of_Licence && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">License Limit:</span>{' '}
              <span className="text-blue-700">
                {existingDevices.length} / {clientInfo.No_Of_Licence} devices used
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Existing Devices Table */}
      <div className="w-full min-w-0 rounded-xl border border-gray-100 bg-white p-4 shadow-sm md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FiUserPlus className="text-blue-600" size={24} />
            Existing Devices
          </h2>
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
          >
            <FiPlus size={18} />
            Add New Device
          </button>
        </div>
        {isLoadingDevices ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading devices...</p>
          </div>
        ) : existingDevices.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No devices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">System ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">System Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Created Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {existingDevices.map((device, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{device.SystemID}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{device.SystemName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {device.CreatedDate ? new Date(device.CreatedDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        device.IsActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {device.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleDeleteDevice(device.SystemID)}
                        disabled={isDeleting}
                        className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-xs"
                      >
                        <FiTrash2 size={14} />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      {showAddDeviceModal && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FiPlus className="text-green-600" size={28} />
                Add New Device(s)
              </h2>
              <button
                onClick={() => {
                  setShowAddDeviceModal(false);
                  setDevices([{ systemID: '', systemName: '' }]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {clientInfo?.No_Of_Licence && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Available Licenses:</span>{' '}
                  <span className="text-blue-700">
                    {clientInfo.No_Of_Licence - existingDevices.length} / {clientInfo.No_Of_Licence} available
                  </span>
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                {devices.map((device, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end p-4 border border-gray-200 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        System ID {index + 1}
                      </label>
                      <input
                        type="text"
                        value={device.systemID}
                        onChange={(e) => updateDevice(index, 'systemID', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter system ID"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        System Name {index + 1}
                      </label>
                      <input
                        type="text"
                        value={device.systemName}
                        onChange={(e) => updateDevice(index, 'systemName', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter system name"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      {devices.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDevice(index)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                        >
                          <FiX size={18} />
                          Remove
                        </button>
                      )}
                      {index === devices.length - 1 && (
                        <button
                          type="button"
                          onClick={addDevice}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2"
                        >
                          <FiPlus size={18} />
                          Add More
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Devices'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDeviceModal(false);
                    setDevices([{ systemID: '', systemName: '' }]);
                  }}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AddClients;
